import { createClient, initialize, Message, User } from '@botpress/webchat-client'
import { EventEmitter } from '@bpinternal/shared'
import { Events } from '../events'
import { getUseMessagesStore, getUseParticipantStore } from '../stores'
import { integrationMessageToBlockMessage } from '../adapters'

type InitResult = {
  on: ReturnType<typeof initialize>
  conversation: { id: string }
  messages: Message[]
  participants: User[]
  user: { id: string; userKey: string }
  client: ReturnType<typeof createClient>
  onCleanup: () => void
}

export const initializeConversation = async ({
  apiUrl,
  clientId,
  adminSecret,
  clientHeaders,
  emitter,
  conversationId,
  userToken,
}: {
  apiUrl: string
  clientId: string
  emitter: EventEmitter<Events>
  clientHeaders?: Record<string, string>
  adminSecret?: string
  conversationId?: string
  userToken?: string
}): Promise<InitResult> => {
  return new Promise((resolve) => {
    const on = initialize({
      apiUrl,
      clientId,
      userToken,
      conversationId,
      adminSecret,
    })

    const onInitUnsub = on('init', ({ conversation, messages, participants, user }) => {
      const messagesStore = getUseMessagesStore(conversation.id)
      const blockMessages = messages.reverse().map(integrationMessageToBlockMessage).filter(Boolean)
      messagesStore.getState().setMessages(blockMessages)

      const participantsStore = getUseParticipantStore(conversation.id)
      participantsStore.getState().setParticipants(participants)

      const client = createClient({
        clientId,
        apiUrl,
        userKey: user.userKey,
        customHeaders: clientHeaders,
      })

      client
        .createEvent({
          conversationId: conversation.id,
          payload: {
            type: 'conversation_started',
            data: {},
          },
        })
        .then(({ event }) => {
          if ((event.payload.data as Record<string, Record<string, string>>)?.status?.isInitial) {
            emitter.emit('conversation', conversation.id)
          }
        })

      resolve({
        on,
        conversation,
        messages,
        participants,
        user,
        client,
        onCleanup: () => {
          onInitUnsub()
        },
      })
    })
  })
}
